import React from "react";
import { WorkspaceStatus } from "models/Workspace.model";
import { EnvironmentStatus } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import {
  EnvironmentSetupLights,
  type SetupStep,
} from "components/Environment/shared/EnvironmentSetupLights";
import { cn } from "lib/utils";

export const COMPUTE_INACTIVE_STATUS_COLOR = "#a3a3a3";

const COMPUTE_PENDING_STATUS_COLOR = "var(--color-pending-blue)";
const COMPUTE_FAILURE_STATUS_COLOR = "#f87171";

type CloudComputeStatusIndicator = {
  color: string;
  pulse: boolean;
};

export type CloudComputeStatus = WorkspaceStatus | EnvironmentStatus;

type CloudComputeStatusPresentation = {
  label: string;
  workspaceStatus: WorkspaceStatus;
};

const getWorkspaceStatusLabel = (status: WorkspaceStatus): string => {
  switch (status) {
    case WorkspaceStatus.Deploying:
      return "Deploying";
    case WorkspaceStatus.Running:
      return "Running";
    case WorkspaceStatus.Stopping:
      return "Stopping";
    case WorkspaceStatus.Stopped:
      return "Stopped";
    case WorkspaceStatus.Starting:
      return "Starting";
    case WorkspaceStatus.Deleting:
      return "Deleting";
    case WorkspaceStatus.Failure:
      return "Failed";
  }
};

const getCloudComputeStatusPresentation = (
  status: CloudComputeStatus,
): CloudComputeStatusPresentation => {
  if (typeof status === "string") {
    return { label: getWorkspaceStatusLabel(status), workspaceStatus: status };
  }

  switch (status) {
    case EnvironmentStatus.PENDING:
      return { label: "Pending", workspaceStatus: WorkspaceStatus.Deploying };
    case EnvironmentStatus.PREPARING:
      return {
        label: "Preparing",
        workspaceStatus: WorkspaceStatus.Deploying,
      };
    case EnvironmentStatus.PREPARING_FAILED:
      return {
        label: "Preparing failed",
        workspaceStatus: WorkspaceStatus.Failure,
      };
    case EnvironmentStatus.PROVISIONING:
      return {
        label: "Provisioning",
        workspaceStatus: WorkspaceStatus.Deploying,
      };
    case EnvironmentStatus.PROVISIONING_FAILED:
      return {
        label: "Provisioning failed",
        workspaceStatus: WorkspaceStatus.Failure,
      };
    case EnvironmentStatus.BUILDING:
      return { label: "Building", workspaceStatus: WorkspaceStatus.Deploying };
    case EnvironmentStatus.BUILDING_FAILED:
      return {
        label: "Build failed",
        workspaceStatus: WorkspaceStatus.Failure,
      };
    case EnvironmentStatus.RUNNING:
      return { label: "Running", workspaceStatus: WorkspaceStatus.Running };
    case EnvironmentStatus.INSTANCE_LOST:
      return {
        label: "Instance lost",
        workspaceStatus: WorkspaceStatus.Failure,
      };
    case EnvironmentStatus.STOPPING:
      return { label: "Stopping", workspaceStatus: WorkspaceStatus.Stopping };
    case EnvironmentStatus.STOPPING_FAILED:
      return {
        label: "Stop failed",
        workspaceStatus: WorkspaceStatus.Failure,
      };
    case EnvironmentStatus.STOPPED:
      return { label: "Stopped", workspaceStatus: WorkspaceStatus.Stopped };
    case EnvironmentStatus.STARTING:
      return { label: "Starting", workspaceStatus: WorkspaceStatus.Starting };
    case EnvironmentStatus.STARTING_FAILED:
      return {
        label: "Start failed",
        workspaceStatus: WorkspaceStatus.Failure,
      };
    case EnvironmentStatus.TERMINATING:
      return {
        label: "Terminating",
        workspaceStatus: WorkspaceStatus.Deleting,
      };
    case EnvironmentStatus.TERMINATING_FAILED:
      return {
        label: "Terminate failed",
        workspaceStatus: WorkspaceStatus.Failure,
      };
    case EnvironmentStatus.TERMINATED:
      return { label: "Terminated", workspaceStatus: WorkspaceStatus.Stopped };
    case EnvironmentStatus.UNSPECIFIED:
    default:
      return {
        label: "Deploying",
        workspaceStatus: WorkspaceStatus.Deploying,
      };
  }
};

const getCloudComputeStatusIndicator = (
  workspaceStatus: WorkspaceStatus,
): CloudComputeStatusIndicator | null => {
  switch (workspaceStatus) {
    case WorkspaceStatus.Deploying:
    case WorkspaceStatus.Starting:
      return { color: COMPUTE_PENDING_STATUS_COLOR, pulse: true };
    case WorkspaceStatus.Failure:
      return { color: COMPUTE_FAILURE_STATUS_COLOR, pulse: false };
    case WorkspaceStatus.Deleting:
      return { color: COMPUTE_FAILURE_STATUS_COLOR, pulse: true };
    case WorkspaceStatus.Stopping:
      return { color: COMPUTE_INACTIVE_STATUS_COLOR, pulse: true };
    case WorkspaceStatus.Running:
    case WorkspaceStatus.Stopped:
      return null;
  }
};

export const getCloudComputeStatusBadgeColor = (status: CloudComputeStatus) => {
  const { label, workspaceStatus } = getCloudComputeStatusPresentation(status);

  if (workspaceStatus === WorkspaceStatus.Running) {
    return "var(--color-highlight)";
  }
  if (
    label === "Pending" ||
    label === "Preparing" ||
    label === "Starting" ||
    label === "Provisioning" ||
    label === "Building" ||
    workspaceStatus === WorkspaceStatus.Deploying
  ) {
    return COMPUTE_PENDING_STATUS_COLOR;
  }
  if (workspaceStatus === WorkspaceStatus.Stopped || label === "Stopped") {
    return COMPUTE_INACTIVE_STATUS_COLOR;
  }
  if (label === "Terminating" || workspaceStatus === WorkspaceStatus.Failure) {
    return COMPUTE_FAILURE_STATUS_COLOR;
  }
  return "var(--color-secondary)";
};

export const ComputeStatusBadge: React.FC<{
  color: string;
  label: string;
}> = ({ color, label }) => (
  <span
    className="inline-block w-fit max-w-full min-w-0 [text-wrap:balance] text-sm font-semibold uppercase leading-[18px]"
    style={{ color }}
  >
    {label}
  </span>
);

export interface CloudComputeStatusSummaryProps extends React.HTMLAttributes<HTMLSpanElement> {
  indicatorsDisabled: boolean;
  setupIndicatorSteps: readonly SetupStep[];
  status: CloudComputeStatus;
}

export const CloudComputeStatusSummary: React.FC<
  CloudComputeStatusSummaryProps
> = ({
  indicatorsDisabled,
  setupIndicatorSteps,
  status,
  className,
  ...props
}) => {
  const presentation = getCloudComputeStatusPresentation(status);
  const { label, workspaceStatus } = presentation;
  const statusIndicator = getCloudComputeStatusIndicator(workspaceStatus);

  return (
    <span
      {...props}
      className={`inline-flex w-fit max-w-full min-w-0 items-center gap-1.5 leading-[24px] ${className || ""}`}
    >
      <ComputeStatusBadge
        color={getCloudComputeStatusBadgeColor(status)}
        label={label}
      />
      {statusIndicator ? (
        <span
          className="inline-flex size-[0.55rem] shrink-0 items-center"
          role="img"
          aria-label={`${label} status`}
        >
          <span
            aria-hidden="true"
            className={cn(
              "size-[0.55rem] rounded-full",
              statusIndicator.pulse &&
                "animate-pulse motion-reduce:animate-none",
            )}
            style={{
              backgroundColor: statusIndicator.color,
              boxShadow:
                statusIndicator.color === COMPUTE_INACTIVE_STATUS_COLOR
                  ? undefined
                  : `0 0 8px ${statusIndicator.color}`,
            }}
          />
        </span>
      ) : (
        <EnvironmentSetupLights
          steps={setupIndicatorSteps}
          disabled={indicatorsDisabled}
        />
      )}
    </span>
  );
};
